/**
 * HeroCoverBoundaryOverlay — stroke-only site boundary preview for
 * HeroImageCover. Mirrors the sidebar thumbnail geometry (260×130
 * viewBox, shared bbox fit) but omits fills so only parcel outlines
 * read against the hero scrim.
 */
import React from 'react';
import { materializeSiteBoundaries, computeAggregate } from '../../../../lib/siteBoundaryGeometry';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { SiteLayer } from '../../../../types/site';
import { cn } from '@/lib/utils';
import { colorForCategory } from '../../../../config/siteLayerColors';
import { isCoverDebugEnabled } from '../../../../lib/runtimeFlags';

const VIEW_W = 260;
const VIEW_H = 130;
const PAD = 0;
const LABEL_FONT_SIZE = 5.5;
const LABEL_CHAR_W = 3.35;
const LABEL_H = 9;
const LABEL_PAD = 2;
const LABEL_MARGIN = 3;
const MIN_LEADER_LENGTH = 12;
const MAX_LEADER_LENGTH = 72;
const LABEL_DISTANCE_TARGET = 30;
const BEAM_WIDTH = 18;
const MAX_OPTIONS_PER_LABEL = 8;
/**
 * LABEL-DECLUTTER — hard cap on the number of leader-line NAME labels the
 * overlay will place. The beam-search force-places EVERY surviving candidate
 * (see `buildForcedOption`/`buildHardFallbackOption`), so an uncapped, undeduped
 * candidate list guarantees collisions on dense KMZ imports (e.g. 'Alternative
 * Access' x3, every setback / flood zone / fiber spur). After dedup + priority
 * sort the list is sliced to this cap BEFORE the beam search so only the
 * highest-value features (site polygons, substations, primary access, the
 * nearest transmission) keep leaders; the rest are dropped from the diagram
 * (their geometry still draws, and lines still route to the utility legend).
 * The default is intentionally legible (≈10); callers tune it via `maxLabels`.
 */
export const DEFAULT_MAX_LEADER_LABELS = 10;
/**
 * LABEL-DECLUTTER — name regex for infrastructure-CRITICAL features that must
 * survive the cap (a substation must never silently disappear). Matched against
 * the (normalized) layer name; a hit bumps `semanticScore` to the top tier so
 * these rank above generic contained-asset polygons, points, and lines.
 */
const CRITICAL_INFRA_HINT = /substation|switchyard|switch\s*yard|power\s*plant|generat|interconnect/i;
const AREA_LABEL_FONT_SIZE = 4.8;
const AREA_LABEL_CHAR_W = 2.95;
const LEADER_ELBOW_STANDOFF = 3.5;
const LINE_ANCHOR_EPSILON_PX = 1.0;
const ACRES_PER_SQ_METER = 1 / 4046.8564224;
const AREA_TEXT_EDGE_CLEARANCE_PX = 4.5;
const LEADER_SOFT_MAX = 54;
const LEADER_RAMP_PENALTY = 1.8;
const CROSS_CANVAS_SOFT_X = 88;
const COMPACTION_GAP_Y = LABEL_H + 2;
const COMPACTION_GAP_X = 6;
const UTILITY_LINE_HINT = /transmission|pipeline|pipe|kV|electric|natural gas|water main|sewer|fiber/i;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface XYPoint { x: number; y: number }
type LayerRole = 'container' | 'asset';
export type Lane = 'left' | 'right' | 'top' | 'bottom';

function rectIntersects(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function inflateRect(r: Rect, pad: number): Rect {
  return { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
}

function pointInRect(p: XYPoint, r: Rect): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

function orientation(a: XYPoint, b: XYPoint, c: XYPoint): number {
  const v = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
  if (Math.abs(v) < 1e-9) return 0;
  return v > 0 ? 1 : 2;
}

function onSegment(a: XYPoint, b: XYPoint, c: XYPoint): boolean {
  return b.x <= Math.max(a.x, c.x) && b.x >= Math.min(a.x, c.x) && b.y <= Math.max(a.y, c.y) && b.y >= Math.min(a.y, c.y);
}

export function segmentsIntersect(p1: XYPoint, q1: XYPoint, p2: XYPoint, q2: XYPoint): boolean {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;
  return false;
}

function laneAxisValue(anchor: XYPoint, lane: Lane): number {
  return lane === 'left' || lane === 'right' ? anchor.y : anchor.x;
}

export function laneAxisRank(anchor: XYPoint, lane: Lane, peers: XYPoint[]): number {
  const axis = laneAxisValue(anchor, lane);
  let idx = 0;
  for (const peer of peers) {
    if (laneAxisValue(peer, lane) < axis) idx++;
  }
  return idx;
}

function rectIntersectsSegment(rect: Rect, a: XYPoint, b: XYPoint): boolean {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  if (maxX < rect.x || minX > rect.x + rect.w || maxY < rect.y || minY > rect.y + rect.h) {
    return false;
  }
  if (pointInRect(a, rect) || pointInRect(b, rect)) return true;
  const tl = { x: rect.x, y: rect.y };
  const tr = { x: rect.x + rect.w, y: rect.y };
  const br = { x: rect.x + rect.w, y: rect.y + rect.h };
  const bl = { x: rect.x, y: rect.y + rect.h };
  return (
    segmentsIntersect(a, b, tl, tr)
    || segmentsIntersect(a, b, tr, br)
    || segmentsIntersect(a, b, br, bl)
    || segmentsIntersect(a, b, bl, tl)
  );
}

function anyLeaderSegmentHitsRects(
  leaderSegs: Array<{ a: XYPoint; b: XYPoint }>,
  rects: Rect[],
): boolean {
  return rects.some((rect) =>
    leaderSegs.some((seg) => rectIntersectsSegment(inflateRect(rect, 0.8), seg.a, seg.b)));
}

function segmentsFromPath(points: XYPoint[]): Array<{ a: XYPoint; b: XYPoint }> {
  const segs: Array<{ a: XYPoint; b: XYPoint }> = [];
  for (let i = 1; i < points.length; i++) {
    segs.push({ a: points[i - 1], b: points[i] });
  }
  return segs;
}

function pointToSegmentDistance(point: XYPoint, segmentStart: XYPoint, segmentEnd: XYPoint): number {
  const dx = segmentEnd.x - segmentStart.x;
  const dy = segmentEnd.y - segmentStart.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq <= 1e-9) return Math.hypot(point.x - segmentStart.x, point.y - segmentStart.y);
  const t = Math.max(0, Math.min(1, ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) / lenSq));
  const projX = segmentStart.x + t * dx;
  const projY = segmentStart.y + t * dy;
  return Math.hypot(point.x - projX, point.y - projY);
}

function isPointNearAnySegment(
  point: XYPoint,
  segments: Array<{ a: XYPoint; b: XYPoint }>,
  epsilon: number,
): boolean {
  return segments.some((seg) => pointToSegmentDistance(point, seg.a, seg.b) <= epsilon);
}

function pointInPolygon(point: { lat: number; lng: number }, polygonInput: { lat: number; lng: number }[]): boolean {
  // HOTFIX(lat-crash): callers pass raw KMZ `layer.geometry.coordinates`, which
  // can contain nullish / non-finite vertices. Filter first so `polygon[i].lat`
  // can't throw "reading 'lat'".
  const polygon = Array.isArray(polygonInput) ? polygonInput.filter(isLatLng) : [];
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersects = ((yi > point.lat) !== (yj > point.lat))
      && (point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || 1e-9) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInProjectedPolygon(point: XYPoint, polygon: XYPoint[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersects = ((yi > point.y) !== (yj > point.y))
      && (point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-9) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function minDistanceToPolygonEdge(point: XYPoint, polygon: XYPoint[]): number {
  if (polygon.length < 2) return Number.POSITIVE_INFINITY;
  const edges = toSegments(polygon, true);
  let minDistance = Number.POSITIVE_INFINITY;
  for (const edge of edges) {
    minDistance = Math.min(minDistance, pointToSegmentDistance(point, edge.a, edge.b));
  }
  return minDistance;
}

function rectIntersectsPolygon(rect: Rect, polygon: XYPoint[]): boolean {
  if (polygon.length < 3) return false;
  const corners: XYPoint[] = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.w, y: rect.y },
    { x: rect.x + rect.w, y: rect.y + rect.h },
    { x: rect.x, y: rect.y + rect.h },
  ];
  if (corners.some((corner) => pointInProjectedPolygon(corner, polygon))) return true;
  if (polygon.some((p) => pointInRect(p, rect))) return true;
  const edges = toSegments(polygon, true);
  return edges.some((edge) => rectIntersectsSegment(rect, edge.a, edge.b));
}

function polygonAreaSqMeters(coordsInput: { lat: number; lng: number }[]): number {
  // HOTFIX(lat-crash): filter nullish / non-finite vertices from raw KMZ coords.
  const coords = Array.isArray(coordsInput) ? coordsInput.filter(isLatLng) : [];
  if (coords.length < 3) return 0;
  const avgLat = coords.reduce((sum, p) => sum + p.lat, 0) / coords.length;
  const r = 6371008.8; // mean Earth radius in meters
  const latScale = (Math.PI / 180) * r;
  const lngScale = (Math.PI / 180) * r * Math.cos((avgLat * Math.PI) / 180);
  let area = 0;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lng * lngScale;
    const yi = coords[i].lat * latScale;
    const xj = coords[j].lng * lngScale;
    const yj = coords[j].lat * latScale;
    area += xj * yi - xi * yj;
  }
  return Math.abs(area) / 2;
}

function polygonAreaProjectedSqPx(points: XYPoint[]): number {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    area += points[j].x * points[i].y - points[i].x * points[j].y;
  }
  return Math.abs(area) / 2;
}

function formatAreaLabel(areaSqM: number): string {
  if (!Number.isFinite(areaSqM) || areaSqM <= 0) return '';
  const acres = areaSqM * ACRES_PER_SQ_METER;
  const precision = acres >= 10 ? 1 : 2;
  return `${acres.toFixed(precision)} ac`;
}

function findBottomLeftInteriorPoint(polygon: XYPoint[]): XYPoint | null {
  if (polygon.length < 3) return null;
  const minX = Math.min(...polygon.map((p) => p.x));
  const maxY = Math.max(...polygon.map((p) => p.y));
  const probes = [
    { x: minX + 2, y: maxY - 2 },
    { x: minX + 4, y: maxY - 4 },
    { x: minX + 6, y: maxY - 6 },
    { x: minX + 10, y: maxY - 6 },
    { x: minX + 10, y: maxY - 10 },
    { x: minX + 14, y: maxY - 12 },
  ];
  return probes.find((p) => pointInProjectedPolygon(p, polygon)) ?? null;
}

function isLatLng(p: unknown): p is { lat: number; lng: number } {
  return (
    !!p &&
    typeof p === 'object' &&
    Number.isFinite((p as { lat?: unknown }).lat) &&
    Number.isFinite((p as { lng?: unknown }).lng)
  );
}

function layerCentroid(layer: Pick<SiteLayer, 'geometry'>): { lat: number; lng: number } | null {
  const coords = layer.geometry.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  if (layer.geometry.type === 'point') {
    // HOTFIX(lat-crash): a degenerate KMZ point layer can have a nullish
    // coords[0] (length 1 but undefined element). Consumers read centroid.lat,
    // so return null (every caller already null-checks) rather than leak an
    // undefined coordinate.
    return isLatLng(coords[0]) ? coords[0] : null;
  }
  // HOTFIX(lat-crash): drop any nullish / non-finite vertices before summing
  // so a degenerate vertex can't NaN the centroid (and can't throw on .lat).
  const valid = coords.filter(isLatLng);
  if (valid.length === 0) return null;
  const sum = valid.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / valid.length, lng: sum.lng / valid.length };
}

function normalizeLayerName(name: string): string {
  return name
    .replace(/\bsub\s+station\b/gi, 'Substation')
    .replace(/\s+/g, ' ')
    .trim();
}

function toProjectedPoints(pointsText: string): XYPoint[] {
  return pointsText
    .split(' ')
    .map((xy) => xy.trim())
    .filter(Boolean)
    .map((xy) => {
      const [x, y] = xy.split(',').map(Number);
      return { x, y };
    })
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

function toSegments(points: XYPoint[], closed: boolean): Array<{ a: XYPoint; b: XYPoint }> {
  const segs: Array<{ a: XYPoint; b: XYPoint }> = [];
  for (let i = 1; i < points.length; i++) {
    segs.push({ a: points[i - 1], b: points[i] });
  }
  if (closed && points.length > 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.x !== last.x || first.y !== last.y) {
      segs.push({ a: last, b: first });
    }
  }
  return segs;
}

function boundaryToSvgPoints(
  boundary: { lat: number; lng: number }[],
  w: number,
  h: number,
  pad: number,
  bbox?: { south: number; west: number; north: number; east: number },
): string | null {
  // HOTFIX(lat-crash): drop nullish / non-finite vertices first so a degenerate
  // KMZ coordinate (undefined element) can't throw on `.lat` here.
  const safeBoundary = Array.isArray(boundary) ? boundary.filter(isLatLng) : [];
  if (safeBoundary.length < 1) return null;
  boundary = safeBoundary;
  const lats = boundary.map(p => p.lat);
  const lngs = boundary.map(p => p.lng);
  const minLat = bbox?.south ?? Math.min(...lats);
  const maxLat = bbox?.north ?? Math.max(...lats);
  const minLng = bbox?.west ?? Math.min(...lngs);
  const maxLng = bbox?.east ?? Math.max(...lngs);
  const spanLat = (maxLat - minLat) || 1e-6;
  const spanLng = (maxLng - minLng) || 1e-6;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  // Contain-fit: preserve aspect ratio without clipping.
  const fit = Math.min(innerW / spanLng, innerH / spanLat);
  const usedW = spanLng * fit;
  const usedH = spanLat * fit;
  const xOffset = pad + (innerW - usedW) / 2;
  const yOffset = pad + (innerH - usedH) / 2;
  return boundary
    .map(p => {
      const x = xOffset + (p.lng - minLng) * fit;
      const y = yOffset + (maxLat - p.lat) * fit;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export interface HeroCoverBoundaryOverlayProps {
  report: Pick<
    SiteRagReport,
    | 'siteBoundary'
    | 'siteBoundaries'
    | 'siteBoundaryAggregate'
    | 'siteArea'
    | 'sitePerimeter'
    | 'siteCentroid'
  >;
  layers?: Array<Pick<SiteLayer, 'id' | 'name' | 'category' | 'geometry' | 'color'>>;
  layerLabels?: Array<{ id: string; name: string }>;
  textTone: 'on-dark' | 'on-light';
  className?: string;
  /**
   * SBPV-PALETTE — opt-in per-parcel boundary stroke colours, indexed by
   * parcel position (wraps modulo the array length). When supplied, each
   * parcel outline is drawn in `parcelStrokeColors[i % len]` so a host
   * legend can match the drawn boundary colours. When OMITTED, parcels
   * fall back to the tone-derived `strokeClass` (white on dark / slate on
   * light) — i.e. the report hero cover is byte-for-byte unchanged. The
   * report cover never passes this prop; only the SITE BOUNDARY landing
   * panel does, where the bottom per-parcel legend uses the same palette.
   */
  parcelStrokeColors?: string[];
  /**
   * HERO-BG (#93) — explicit single stroke colour for ALL parcel outlines,
   * used when the diagram is painted as a full-bleed hero background and the
   * author picks a line colour. Lower precedence than `parcelStrokeColors`
   * (which is per-parcel and host-legend-matched); higher precedence than the
   * tone-derived `strokeClass`. When OMITTED, behaviour is unchanged.
   */
  strokeColor?: string;
  /**
   * HERO-BG (#93) — ordered hex stops for a top→bottom gradient FILL applied
   * to every parcel polygon (full-bleed hero usage). Requires ≥2 stops; when
   * fewer/omitted, parcels stay unfilled (`fill="none"`) exactly as before.
   */
  fillGradient?: string[];
  /**
   * SBPV-THIN (#90-revert) — multiplier applied to every GEOMETRY stroke
   * width (parcel outlines, KMZ polygon outlines, pipeline/transmission
   * lines and the utility-legend dashes). Defaults to `1` so the report
   * hero cover and every other caller stay byte-for-byte identical (the
   * pinned snapshot keeps 1.75 / 1.35 / 1.3). The SITE BOUNDARY landing
   * panel passes a value < 1 to draw THINNER overlay strokes now that the
   * card renders this bespoke SVG diagram in place of the (too-thick) live
   * Google-Maps polyline overlay. Leader-line + label stroke widths (text
   * outlines) are deliberately NOT scaled — only the drawn map geometry.
   */
  strokeScale?: number;
  /**
   * LABEL-DECLUTTER — leader-label density control per surface.
   *   - `'full'` (default): dedup + prioritize + cap to `maxLabels`. The hero
   *     cover, the SITE BOUNDARY landing panel, and the PropertyMapContent
   *     SVG fallback all use this — a legible, deduped, capped set.
   *   - `'none'`: place NO leader-line NAME labels at all (geometry + the
   *     utility legend + area chips still draw). Use for compact / card-sized
   *     mounts where leaders would be illegible.
   * Geometry is NEVER affected by this prop — only the leader NAME labels.
   */
  labelsMode?: 'full' | 'none';
  /**
   * LABEL-DECLUTTER — explicit cap on the number of leader-line NAME labels in
   * `'full'` mode. Defaults to {@link DEFAULT_MAX_LEADER_LABELS}. Clamped to a
   * sane positive integer; ignored when `labelsMode === 'none'`. Lower values
   * declutter denser surfaces; the dedup + priority sort guarantees the
   * surviving labels are the highest-value features (polygons + substations
   * first).
   */
  maxLabels?: number;
  /**
   * HERO-COVER CLEAN-MAP — suppress EVERY text annotation on the diagram
   * (leader NAME labels, the per-polygon area "ac" labels, AND the utility
   * legend chip names) so ONLY the boundary outline + layer geometry render.
   * Stronger than `labelsMode='none'` (which only drops the leader NAME
   * labels — the area chips and utility legend still draw). The report hero
   * cover sets this so the larger cover map reads clean rather than crowded
   * with "Flood — part 2 of 4" / "Entergy 230kV" / "5mi. Fiber Ext." text.
   * Geometry is NEVER affected — only the text. Defaults `false` so every
   * other caller (SITE BOUNDARY panel, PropertyMapContent) is unchanged.
   */
  hideAllLabels?: boolean;
}

export function HeroCoverBoundaryOverlay({
  report,
  layers = [],
  layerLabels = [],
  textTone,
  className,
  parcelStrokeColors,
  strokeColor,
  fillGradient,
  strokeScale = 1,
  labelsMode = 'full',
  maxLabels,
  hideAllLabels = false,
}: HeroCoverBoundaryOverlayProps) {
  // HERO-COVER CLEAN-MAP — `hideAllLabels` forces the leader cap to 0 (same as
  // `labelsMode='none'`) AND, further down, suppresses the area chips + utility
  // legend so the diagram renders geometry-only.
  const labelsSuppressed = hideAllLabels || labelsMode === 'none';
  // LABEL-DECLUTTER — resolve the leader-label cap. `'none'` ⇒ 0 (no leader
  // NAME labels at all). `'full'` ⇒ the explicit `maxLabels` (clamped to a
  // sane positive integer) or the legible default. Geometry / legend / area
  // chips are never gated by this — only the leader NAME labels.
  const leaderLabelCap = labelsSuppressed
    ? 0
    : (Number.isFinite(maxLabels) && (maxLabels as number) > 0
        ? Math.max(1, Math.floor(maxLabels as number))
        : DEFAULT_MAX_LEADER_LABELS);
  // SBPV-THIN (#90-revert) — clamp to a sane positive range so a stray 0 /
  // negative / NaN can never blank the outlines or invert them. Default 1
  // is a no-op (snapshot + every existing caller unchanged).
  const geomStrokeScale = Number.isFinite(strokeScale) && strokeScale > 0
    ? Math.min(strokeScale, 4)
    : 1;
  const parcels = React.useMemo(
    () =>
      materializeSiteBoundaries({
        siteBoundaries: report.siteBoundaries,
        siteBoundary: report.siteBoundary,
        siteArea: report.siteArea,
        sitePerimeter: report.sitePerimeter,
        siteCentroid: report.siteCentroid,
      }),
    [
      report.siteBoundaries,
      report.siteBoundary,
      report.siteArea,
      report.sitePerimeter,
      report.siteCentroid,
    ],
  );

  const aggregate = React.useMemo(() => {
    if (parcels.length === 0) return null;
    if (report.siteBoundaryAggregate) return report.siteBoundaryAggregate;
    return computeAggregate(parcels);
  }, [parcels, report.siteBoundaryAggregate]);

  const onDark = textTone === 'on-dark';
  const coverDebugEnabled = isCoverDebugEnabled();
  const strokeClass = onDark ? 'stroke-white' : 'stroke-slate-900';
  // HERO-BG (#93) — full-bleed recolour. `strokeColor` overrides the
  // tone-derived stroke class; `fillGradient` (≥2 stops) paints a parcel fill
  // via a per-instance <defs> gradient. Both default to off → unchanged output.
  const HERO_FILL_GRADIENT_PREFIX = 'hero-bg-fill';
  const gradientStops = (fillGradient ?? []).map((c) => c?.trim() || '').filter(Boolean);
  const hasFillGradient = gradientStops.length >= 2;
  const fillGradientId = React.useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const parcelFillUrl = hasFillGradient
    ? `url(#${HERO_FILL_GRADIENT_PREFIX}-${fillGradientId})`
    : 'none';
  const isUtilityLineLayer = (layer: Pick<SiteLayer, 'name' | 'category' | 'geometry'>): boolean => {
    if (layer.geometry.type !== 'line') return false;
    const category = layer.category.toLowerCase();
    if (category.includes('utility') || category.startsWith('infrastructure')) return true;
    return UTILITY_LINE_HINT.test(layer.name);
  };
  const utilityLegend = layers
    .filter((layer) => isUtilityLineLayer(layer))
    .map((layer) => ({
      id: layer.id,
      name: layer.name,
      color: colorForCategory(layer.category, layer.color),
      lineWidth: 1.3 * geomStrokeScale,
    }));
  const utilityLineIds = new Set(utilityLegend.map((entry) => entry.id));
  // BBOX helper — extent of an arbitrary lat/lng coordinate list (null when empty).
  // HOTFIX(lat-crash): filter nullish / non-finite vertices from raw KMZ coords.
  const coordsBbox = (
    coordsInput: Array<{ lat: number; lng: number }>,
  ): { south: number; west: number; north: number; east: number } | null => {
    const coords = Array.isArray(coordsInput) ? coordsInput.filter(isLatLng) : [];
    return coords.length > 0
      ? {
          south: Math.min(...coords.map((p) => p.lat)),
          west: Math.min(...coords.map((p) => p.lng)),
          north: Math.max(...coords.map((p) => p.lat)),
          east: Math.max(...coords.map((p) => p.lng)),
        }
      : null;
  };
  // #FIT-PRIMARY — anchor the projection window on the PRIMARY site boundary =
  // the LARGEST parcel (by bbox area), NOT the union of every parcel + every
  // polygon layer. Before this, the area base was `aggregate.bbox (ALL parcels)`
  // ∪ `layerBbox (ALL polygon layers)`; a KMZ that imports a DISTANT feature —
  // a far substation polygon, a pipeline-buffer parcel, or (via the line clamp)
  // region-spanning transmission/fiber lines — dragged that base out to the
  // whole region and the real site collapsed to a dot. Anchoring on the largest
  // parcel and CLAMPING every other geometry (below) keeps the site readable.
  // When there are no parcels we fall back to the polygon/point base.
  const primaryParcel = (() => {
    let best: (typeof parcels)[number] | null = null;
    let bestArea = -1;
    for (const parcel of parcels) {
      const b = coordsBbox(parcel.polygon);
      if (!b) continue;
      const area = (b.north - b.south) * (b.east - b.west);
      if (area > bestArea) {
        bestArea = area;
        best = parcel;
      }
    }
    return best;
  })();
  const primaryParcelBbox = primaryParcel ? coordsBbox(primaryParcel.polygon) : null;
  const polygonAreaCoords = layers
    .filter((layer) => layer.geometry.type === 'polygon')
    .flatMap((layer) => layer.geometry.coordinates);
  const layerBbox = coordsBbox(polygonAreaCoords);
  // #101 — the fit bbox must NOT include line-type geometries (pipelines /
  // transmission lines): a long pipeline tail otherwise drags the frame out
  // to the whole region and the parcels shrink to a dot. Lines still render
  // as overlays via drawnSegments; they just don't drive the zoom.
  const nonLineCoords = layers
    .filter((layer) => layer.geometry.type !== 'line')
    .flatMap((layer) => layer.geometry.coordinates);
  const nonLineBbox = coordsBbox(nonLineCoords);
  // True last resort: a line-only KMZ (no parcels, no point/polygon layers)
  // still needs SOME bbox or nothing renders at all. Lines drive the zoom
  // only when they are the ONLY geometry present.
  const allCoords = layers.flatMap((layer) => layer.geometry.coordinates);
  const fallbackBbox = coordsBbox(allCoords);
  // #FIT-PRIMARY — union of EVERY non-primary geometry: secondary parcels
  // (incl. pipeline-buffer polygons mis-classified as parcels), ALL polygon
  // layers (incl. a distant substation polygon), ALL point layers (incl. a
  // far substation/fiber marker), and ALL line layers (transmission / fiber).
  // This is the generalization of the old line-only `lineBbox`: it is unioned
  // into the primary box with the SAME bounded per-axis clamp (below), so a
  // nearby feature comes fully into frame while a region-spanning feature is
  // bounded (its far tail may exit) and the site never collapses to a dot.
  const nonPrimaryCoords = [
    // Secondary parcels (the primary IS the anchor, so it is excluded here).
    ...parcels
      .filter((parcel) => parcel !== primaryParcel)
      .flatMap((parcel) => parcel.polygon),
    // Every layer geometry (polygon / point / line).
    ...allCoords,
  ];
  const nonPrimaryBbox = coordsBbox(nonPrimaryCoords);
  // Max fraction of the PRIMARY-parcel span any non-primary geometry may push
  // each side of the fit box. A feature wholly inside the primary box (the
  // common case) leaves the box UNCHANGED (snapshot-safe); a modestly
  // overhanging feature comes fully into frame; a region-spanning feature is
  // bounded (its far tail may still exit) so the site stays readable.
  //
  // #FIT-PRIMARY — value lowered from the historical 0.6. The 0.6 cap was set
  // in the lines-only era (#101) where the ONLY thing it clamped was a single
  // pipeline tail. It now governs EVERY non-primary geometry, including very
  // distant points/lines (the redhead KMZ imports a Long-haul Fiber corridor
  // ≈9–12 km off, Entergy 230/500 kV lines ≈8 km wide, and a Moss Bluff
  // substation point ≈2.9 km away — see the probe). At 0.6 those far features
  // each pushed their side out by 0.6× the primary span, growing the window to
  // ~2.2× the primary per axis; the contain-fit then squeezed the site to
  // ~19 % of the frame WIDTH (a "tiny cluster on the left" while the leaders
  // fanned across the empty right) — exactly the reported collapse. The probe
  // also showed every LEGITIMATE site feature (all secondary parcels + ALL
  // polygon layers: setbacks, flood zones, planned substation/expansion) lies
  // ENTIRELY inside the primary parcel bbox (0.000 overhang on all four sides),
  // so the box only ever grew because of the distant point/line geometry. A
  // tighter 0.15 cap (window ≤ 1.3× primary per axis) keeps the real site
  // filling a healthy fraction of the frame, still admits a genuinely
  // overhanging feature up to 15 % of the primary span, and is a no-op for the
  // common "everything inside the parcel" case (committed snapshots unchanged).
  const MAX_LINE_EXPANSION = 0.15;
  const combinedBbox = (() => {
    // Anchor base: the LARGEST parcel. When there are no parcels at all, fall
    // back to the polygon/point base (line-only KMZ falls back to all coords).
    const anchorBbox = primaryParcelBbox
      ?? layerBbox
      ?? nonLineBbox
      ?? null;
    if (!anchorBbox) return fallbackBbox ?? undefined;
    if (!nonPrimaryBbox) return anchorBbox;
    // Union the non-primary extent into the anchor base, CLAMPED per axis
    // against the FIXED anchor span (never the growing result — that would
    // compound). Geometry wholly inside the anchor is a no-op (keeps the
    // committed snapshots byte-identical).
    const spanLat = Math.max(anchorBbox.north - anchorBbox.south, 1e-6);
    const spanLng = Math.max(anchorBbox.east - anchorBbox.west, 1e-6);
    const clampLo = (lo: number, otherLo: number, span: number) =>
      Math.min(lo, Math.max(otherLo, lo - MAX_LINE_EXPANSION * span));
    const clampHi = (hi: number, otherHi: number, span: number) =>
      Math.max(hi, Math.min(otherHi, hi + MAX_LINE_EXPANSION * span));
    return {
      south: clampLo(anchorBbox.south, nonPrimaryBbox.south, spanLat),
      north: clampHi(anchorBbox.north, nonPrimaryBbox.north, spanLat),
      west: clampLo(anchorBbox.west, nonPrimaryBbox.west, spanLng),
      east: clampHi(anchorBbox.east, nonPrimaryBbox.east, spanLng),
    };
  })();
  const drawnSegments = React.useMemo(() => {
    const segs: Array<{ a: XYPoint; b: XYPoint }> = [];
    for (const parcel of parcels) {
      const txt = boundaryToSvgPoints(parcel.polygon, VIEW_W, VIEW_H, PAD, combinedBbox);
      if (!txt) continue;
      segs.push(...toSegments(toProjectedPoints(txt), true));
    }
    for (const layer of layers) {
      const txt = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
      if (!txt) continue;
      const projected = toProjectedPoints(txt);
      if (layer.geometry.type === 'polygon') segs.push(...toSegments(projected, true));
      if (layer.geometry.type === 'line') segs.push(...toSegments(projected, false));
    }
    return segs;
  }, [parcels, layers, combinedBbox]);

  // HOOKS-ORDER (crash fix): this "nothing to draw" early return MUST sit after
  // EVERY hook above (parcels / aggregate / drawnSegments useMemo). It used to
  // live right after `aggregate`, BEFORE the `drawnSegments` useMemo — harmless
  // on the report cover (always has boundary data) but, now that the SITE
  // BOUNDARY panel mounts this overlay always-on, a site with no boundary/layers
  // took the early return and SKIPPED the drawnSegments hook → React crashed with
  // "Rendered fewer hooks than expected." All hooks now run unconditionally first.
  if (!aggregate && parcels.length === 0 && layers.length === 0) return null;
  const projectPoint = (
    point: { lat: number; lng: number },
    bbox: { south: number; west: number; north: number; east: number } | undefined,
  ): { x: number; y: number } => {
    const minLat = bbox?.south ?? point.lat;
    const maxLat = bbox?.north ?? point.lat + 1e-6;
    const minLng = bbox?.west ?? point.lng;
    const maxLng = bbox?.east ?? point.lng + 1e-6;
    const spanLat = (maxLat - minLat) || 1e-6;
    const spanLng = (maxLng - minLng) || 1e-6;
    const innerW = VIEW_W - PAD * 2;
    const innerH = VIEW_H - PAD * 2;
    const fit = Math.min(innerW / spanLng, innerH / spanLat);
    const usedW = spanLng * fit;
    const usedH = spanLat * fit;
    const xOffset = PAD + (innerW - usedW) / 2;
    const yOffset = PAD + (innerH - usedH) / 2;
    return {
      x: xOffset + (point.lng - minLng) * fit,
      y: yOffset + (maxLat - point.lat) * fit,
    };
  };
  const inFrame = (p: XYPoint): boolean =>
    p.x >= LABEL_MARGIN && p.x <= VIEW_W - LABEL_MARGIN && p.y >= LABEL_MARGIN && p.y <= VIEW_H - LABEL_MARGIN;
  const clampToFrame = (p: XYPoint): XYPoint => ({
    x: Math.min(VIEW_W - LABEL_MARGIN, Math.max(LABEL_MARGIN, p.x)),
    y: Math.min(VIEW_H - LABEL_MARGIN, Math.max(LABEL_MARGIN, p.y)),
  });
  const representativeAnchorForLayer = (layer: Pick<SiteLayer, 'geometry'>): XYPoint | null => {
    const pointsText = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
    const projected = pointsText ? toProjectedPoints(pointsText) : [];
    if (projected.length === 0) return null;
    const center = { x: VIEW_W / 2, y: VIEW_H / 2 };
    const visibleVertices = projected.filter(inFrame);
    if (visibleVertices.length > 0) {
      return visibleVertices.reduce((best, p) => {
        const bestD = (best.x - center.x) ** 2 + (best.y - center.y) ** 2;
        const pD = (p.x - center.x) ** 2 + (p.y - center.y) ** 2;
        return pD < bestD ? p : best;
      });
    }
    if (layer.geometry.type === 'line' && projected.length >= 2) {
      const sampled: XYPoint[] = [];
      for (let i = 1; i < projected.length; i++) {
        const a = projected[i - 1];
        const b = projected[i];
        for (let t = 0; t <= 1.0001; t += 0.1) {
          const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
          if (inFrame(p)) sampled.push(p);
        }
      }
      if (sampled.length > 0) {
        return sampled.reduce((best, p) => {
          const bestD = (best.x - center.x) ** 2 + (best.y - center.y) ** 2;
          const pD = (p.x - center.x) ** 2 + (p.y - center.y) ** 2;
          return pD < bestD ? p : best;
        });
      }
      return null;
    }
    const centroid = layerCentroid(layer);
    if (!centroid) return clampToFrame(projected[0]);
    return clampToFrame(projectPoint(centroid, combinedBbox));
  };
  const layerById = new Map(layers.map((l) => [l.id, l]));
  const polygonLayers = layers.filter((l) => l.geometry.type === 'polygon');
  const polygonRoleById = (() => {
    const roles = new Map<string, LayerRole>();
    const centroids = new Map<string, { lat: number; lng: number }>();
    for (const layer of polygonLayers) {
      const centroid = layerCentroid(layer);
      if (centroid) centroids.set(layer.id, centroid);
    }
    for (const layer of polygonLayers) {
      const centroid = centroids.get(layer.id);
      if (!centroid) {
        roles.set(layer.id, 'asset');
        continue;
      }
      const containedByAnother = polygonLayers.some(
        (other) => other.id !== layer.id && pointInPolygon(centroid, other.geometry.coordinates),
      );
      if (containedByAnother) {
        roles.set(layer.id, 'asset');
        continue;
      }
      const lowerName = layer.name.toLowerCase();
      const explicitContainer = /phase|site|parcel|boundary/.test(lowerName);
      roles.set(layer.id, explicitContainer ? 'container' : 'asset');
    }
    return roles;
  })();
  const semanticScore = (layer: Pick<SiteLayer, 'id' | 'name' | 'geometry'>): number => {
    // LABEL-DECLUTTER — infrastructure-critical features (substation /
    // switchyard / power plant / generator / interconnect) ALWAYS rank top
    // so they survive the cap regardless of geometry type. A substation point
    // or polygon must never be dropped in favour of a setback or flood zone.
    if (CRITICAL_INFRA_HINT.test(layer.name)) return 200;
    if (layer.geometry.type !== 'polygon') {
      return layer.geometry.type === 'line' ? 70 : 60;
    }
    const centroid = layerCentroid(layer);
    if (!centroid) return 40;
    const contained = polygonLayers.some((p) => p.id !== layer.id && pointInPolygon(centroid, p.geometry.coordinates));
    return contained ? 100 : 40;
  };
  const anchorsForLayer = (layer: Pick<SiteLayer, 'geometry'>): XYPoint[] => {
    const pointsText = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
    const projected = pointsText ? toProjectedPoints(pointsText) : [];
    if (projected.length === 0) return [];
    const anchors: XYPoint[] = [];
    const pushUnique = (p: XYPoint) => {
      const clamped = clampToFrame(p);
      if (anchors.some((q) => Math.hypot(q.x - clamped.x, q.y - clamped.y) < 2.5)) return;
      anchors.push(clamped);
    };
    if (layer.geometry.type !== 'line') {
      const centroid = layerCentroid(layer);
      if (centroid) pushUnique(projectPoint(centroid, combinedBbox));
    }
    const representative = representativeAnchorForLayer(layer);
    if (representative) pushUnique(representative);
    const inFramePoints = projected.filter(inFrame);
    if (layer.geometry.type === 'line') {
      for (const p of inFramePoints) pushUnique(p);
      for (let i = 1; i < projected.length; i++) {
        const a = projected[i - 1];
        const b = projected[i];
        for (const t of [0.2, 0.5, 0.8]) {
          const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
          if (inFrame(p)) pushUnique(p);
        }
      }
    }
    if (layer.geometry.type === 'polygon') {
      const bl = findBottomLeftInteriorPoint(projected);
      if (bl) pushUnique(bl);
      if (inFramePoints.length > 0) {
        const minX = Math.min(...inFramePoints.map((p) => p.x));
        const maxY = Math.max(...inFramePoints.map((p) => p.y));
        pushUnique({ x: minX + 8, y: maxY - 8 });
      }
    }
    if (anchors.length === 0) pushUnique(projected[0]);
    const chosen = anchors.slice(0, 6);
    if (process.env.NODE_ENV !== 'production' && layer.geometry.type === 'line') {
      const segs = toSegments(projected, false);
      for (const anchor of chosen) {
        if (!isPointNearAnySegment(anchor, segs, LINE_ANCHOR_EPSILON_PX)) {
          // eslint-disable-next-line no-console
          console.warn('[HeroCoverBoundaryOverlay] line anchor detached from geometry', { layer, anchor });
        }
      }
    }
    return chosen;
  };

  type CandidateEntry = {
    id: string;
    name: string;
    shortName: string;
    anchors: XYPoint[];
    priority: number;
    geometryType: SiteLayer['geometry']['type'];
    projectedAreaSqPx: number;
    role: LayerRole;
  };
  // LABEL-DECLUTTER — the upstream `layerLabels` list is uncapped, undeduped,
  // and unprioritized (1:1 with every visible layer). We build the raw asset
  // candidate list here, then dedup → prioritize → cap (below) so the
  // beam-search force-placer never has to stack collisions at high counts.
  const rawCandidates: CandidateEntry[] = layerLabels
    .map((label): CandidateEntry | null => {
      const layer = layerById.get(label.id);
      if (!layer) return null;
      if (utilityLineIds.has(layer.id)) return null;
      const anchors = anchorsForLayer(layer);
      if (anchors.length === 0) return null;
      const fullName = normalizeLayerName(label.name);
      let projectedAreaSqPx = 0;
      if (layer.geometry.type === 'polygon') {
        const pointsText = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
        const projected = pointsText ? toProjectedPoints(pointsText) : [];
        projectedAreaSqPx = polygonAreaProjectedSqPx(projected);
      }
      return {
        id: label.id,
        name: fullName,
        shortName: fullName.length > 22 ? `${fullName.slice(0, 19)}...` : fullName,
        anchors,
        priority: semanticScore(layer),
        geometryType: layer.geometry.type,
        projectedAreaSqPx,
        role: layer.geometry.type === 'polygon'
          ? (polygonRoleById.get(layer.id) ?? 'asset')
          : 'asset',
      };
    })
    .filter((x): x is CandidateEntry => x !== null)
    .filter((x) => x.role === 'asset');

  // LABEL-DECLUTTER step 1 — DEDUP by normalized (case-insensitive,
  // whitespace-collapsed) name. Duplicate-named features ('Alternative
  // Access' x3, 'Entergy 500kV' x2 if a KMZ tagged the line as a point)
  // collapse to ONE leader label; the kept instance is the highest-priority,
  // then largest-geometry one so a substation polygon beats its duplicate
  // point. Geometry for every instance still DRAWS — only the redundant NAME
  // labels are removed.
  const dedupedCandidates: CandidateEntry[] = (() => {
    const byKey = new Map<string, CandidateEntry>();
    const order: string[] = [];
    const betterThan = (a: CandidateEntry, b: CandidateEntry): boolean => {
      if (a.priority !== b.priority) return a.priority > b.priority;
      if (a.projectedAreaSqPx !== b.projectedAreaSqPx) return a.projectedAreaSqPx > b.projectedAreaSqPx;
      return false;
    };
    for (const cand of rawCandidates) {
      const key = cand.name.toLowerCase();
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, cand);
        order.push(key);
      } else if (betterThan(cand, existing)) {
        byKey.set(key, cand);
      }
    }
    return order.map((key) => byKey.get(key)!);
  })();

  // LABEL-DECLUTTER step 2 — PRIORITY SORT (infra-critical → contained assets
  // → lines/points → top-level polygons), tie-broken by larger projected
  // geometry then original order, so the cap (step 3) keeps the most relevant
  // features. A stable original-index tiebreak keeps placement deterministic.
  const dedupOriginalIndex = new Map<string, number>(
    dedupedCandidates.map((c, idx) => [c.id, idx] as const),
  );
  const prioritizedCandidates = [...dedupedCandidates].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    if (b.projectedAreaSqPx !== a.projectedAreaSqPx) return b.projectedAreaSqPx - a.projectedAreaSqPx;
    return (dedupOriginalIndex.get(a.id) ?? 0) - (dedupOriginalIndex.get(b.id) ?? 0);
  });

  // LABEL-DECLUTTER step 3 — CAP the leader-label count BEFORE the beam search.
  // Overflow (lowest-priority setbacks / flood zones / fiber / generic access
  // roads) is dropped from the leaders entirely. `labelsMode='none'` ⇒ cap 0.
  const candidates: CandidateEntry[] = prioritizedCandidates.slice(0, leaderLabelCap);
  // LABEL-DECLUTTER step 4 — which candidates may FORCE-PLACE (stack on top of
  // others as a last resort when no collision-free slot exists). The cap above
  // already trims the list to a legible size, so the top of it should always be
  // placed (a substation must never silently disappear). Only the LOW-priority
  // TAIL — the candidates beyond a comfortable force-place budget — is DROPPED
  // instead of force-stacked, which is what reintroduced collisions before.
  // Below the budget every survivor force-places (matches the historical
  // "every label placed" contract for small/medium maps); only genuinely dense
  // maps shed their least-relevant overflow.
  const FORCE_PLACE_BUDGET = 8;
  const forcePlaceIds = new Set(
    candidates.slice(0, Math.min(candidates.length, FORCE_PLACE_BUDGET)).map((c) => c.id),
  );
  const containerPolygonsProjected: XYPoint[][] = (() => {
    const projected: XYPoint[][] = [];
    for (const parcel of parcels) {
      const txt = boundaryToSvgPoints(parcel.polygon, VIEW_W, VIEW_H, PAD, combinedBbox);
      if (!txt) continue;
      const points = toProjectedPoints(txt);
      if (points.length >= 3) projected.push(points);
    }
    for (const layer of polygonLayers) {
      if ((polygonRoleById.get(layer.id) ?? 'asset') !== 'container') continue;
      const txt = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
      if (!txt) continue;
      const points = toProjectedPoints(txt);
      if (points.length >= 3) projected.push(points);
    }
    return projected;
  })();
  const assetPolygonsProjected: XYPoint[][] = polygonLayers
    .filter((layer) => (polygonRoleById.get(layer.id) ?? 'asset') === 'asset')
    .map((layer) => boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox))
    .filter((txt): txt is string => Boolean(txt))
    .map((txt) => toProjectedPoints(txt))
    .filter((points) => points.length >= 3);
  const assetPolygonByLayerId = new Map<string, XYPoint[]>(
    polygonLayers
      .filter((layer) => (polygonRoleById.get(layer.id) ?? 'asset') === 'asset')
      .map((layer) => {
        const pointsText = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
        const points = pointsText ? toProjectedPoints(pointsText) : [];
        return [layer.id, points] as const;
      })
      .filter((entry) => entry[1].length >= 3),
  );
  const siteBoundaryPolygonsProjected: XYPoint[][] = parcels
    .map((parcel) => boundaryToSvgPoints(parcel.polygon, VIEW_W, VIEW_H, PAD, combinedBbox))
    .filter((x): x is string => Boolean(x))
    .map((txt) => toProjectedPoints(txt))
    .filter((points) => points.length >= 3);
  const siteBounds = (() => {
    const points = siteBoundaryPolygonsProjected.flat();
    if (points.length === 0) {
      return {
        minX: LABEL_MARGIN + 16,
        maxX: VIEW_W - LABEL_MARGIN - 16,
        minY: LABEL_MARGIN + 16,
        maxY: VIEW_H - LABEL_MARGIN - 16,
      };
    }
    return {
      minX: Math.min(...points.map((p) => p.x)),
      maxX: Math.max(...points.map((p) => p.x)),
      minY: Math.min(...points.map((p) => p.y)),
      maxY: Math.max(...points.map((p) => p.y)),
    };
  })();
  const buildAreaLabels = (
    blockedRects: Rect[],
    blockedLeaders: Array<{ a: XYPoint; b: XYPoint }>,
  ): { labels: Array<{ id: string; text: string; x: number; y: number }>; rects: Rect[] } => {
    const rects: Rect[] = [];
    const labels: Array<{ id: string; text: string; x: number; y: number }> = [];
    const seenAreaLabels = new Set<string>();
    const sources: Array<{ id: string; coords: { lat: number; lng: number }[] }> = [
      ...layers
        .filter((layer) => layer.geometry.type === 'polygon')
        .map((layer) => ({ id: layer.id, coords: layer.geometry.coordinates })),
      ...parcels.map((parcel) => ({ id: `parcel-${parcel.id}`, coords: parcel.polygon })),
    ];
    for (const source of sources) {
      const pointsText = boundaryToSvgPoints(source.coords, VIEW_W, VIEW_H, PAD, combinedBbox);
      if (!pointsText) continue;
      const projected = toProjectedPoints(pointsText);
      if (projected.length < 3) continue;
      if (polygonAreaProjectedSqPx(projected) < 220) continue;
      const areaText = formatAreaLabel(polygonAreaSqMeters(source.coords));
      if (!areaText) continue;
      const centroid = (() => {
        // HOTFIX(lat-crash): filter nullish / non-finite vertices first.
        const valid = Array.isArray(source.coords) ? source.coords.filter(isLatLng) : [];
        if (valid.length === 0) return null;
        const sum = valid.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
        return { lat: sum.lat / valid.length, lng: sum.lng / valid.length };
      })();
      if (!centroid) continue;
      const fallbackPoint = projectPoint(centroid, combinedBbox);
      const seed = findBottomLeftInteriorPoint(projected) ?? fallbackPoint;
      const textW = Math.max(12, areaText.length * AREA_LABEL_CHAR_W);
      const boxW = textW + 2;
      const boxH = AREA_LABEL_FONT_SIZE + 2;
      const offsets = [
        { dx: 0, dy: 0 },
        { dx: 2, dy: -2 },
        { dx: 4, dy: -4 },
        { dx: 8, dy: -4 },
        { dx: 10, dy: -8 },
        { dx: 14, dy: -10 },
      ];
      for (const offset of offsets) {
        const textX = seed.x + offset.dx;
        const textY = seed.y + offset.dy;
        const box: Rect = {
          x: textX - 1,
          y: textY - AREA_LABEL_FONT_SIZE - 1,
          w: boxW,
          h: boxH,
        };
        const outOfBounds = box.x < LABEL_MARGIN
          || box.y < LABEL_MARGIN
          || box.x + box.w > VIEW_W - LABEL_MARGIN
          || box.y + box.h > VIEW_H - LABEL_MARGIN;
        if (outOfBounds) continue;
        const anchorProbe = { x: textX + 0.8, y: textY - AREA_LABEL_FONT_SIZE * 0.5 };
        if (!pointInProjectedPolygon(anchorProbe, projected)) continue;
        if (drawnSegments.some((seg) => rectIntersectsSegment(inflateRect(box, 0.4), seg.a, seg.b))) continue;
        if (blockedLeaders.some((seg) => rectIntersectsSegment(inflateRect(box, 0.6), seg.a, seg.b))) continue;
        if (blockedRects.some((r) => rectIntersects(r, inflateRect(box, 0.9)))) continue;
        if (rects.some((r) => rectIntersects(r, inflateRect(box, 0.9)))) continue;
        const dedupeKey = `${areaText}:${Math.round(textX / 6)}:${Math.round(textY / 6)}`;
        if (seenAreaLabels.has(dedupeKey)) continue;
        // Also collapse same-value labels that land almost on top of each other.
        const nearDuplicate = labels.some((l) => l.text === areaText && Math.hypot(l.x - textX, l.y - textY) < 18);
        if (nearDuplicate) continue;
        if (minDistanceToPolygonEdge(anchorProbe, projected) < AREA_TEXT_EDGE_CLEARANCE_PX) continue;
        seenAreaLabels.add(dedupeKey);
        rects.push(box);
        labels.push({ id: source.id, text: areaText, x: textX, y: textY });
        break;
      }
    }
    return { labels, rects };
  };
  const occupied: Rect[] = [];
  const placedLeaderSegments: Array<{ a: XYPoint; b: XYPoint }> = [];

  type Annotation = {
    id: string;
    name: string;
    fullName: string;
    anchor: { x: number; y: number };
    role: LayerRole;
    textX: number;
    textY: number;
    textAnchor: 'start' | 'end';
    lineEndX: number;
    lineEndY: number;
    lane?: Lane;
    leaderSegs: Array<{ a: XYPoint; b: XYPoint }>;
  };
  type Placed = Annotation & {
    box: Rect;
    spacingBox: Rect;
    leaderSegs: Array<{ a: XYPoint; b: XYPoint }>;
    score: number;
  };
  type State = {
    score: number;
    annotations: Placed[];
    occupiedRects: Rect[];
    leaderSegs: Array<{ a: XYPoint; b: XYPoint }>;
  };

  const primaryLane = (anchor: XYPoint): Lane => {
    const entries: Array<{ lane: Lane; d: number }> = [
      { lane: 'left', d: Math.abs(anchor.x - siteBounds.minX) },
      { lane: 'right', d: Math.abs(anchor.x - siteBounds.maxX) },
      { lane: 'top', d: Math.abs(anchor.y - siteBounds.minY) },
      { lane: 'bottom', d: Math.abs(anchor.y - siteBounds.maxY) },
    ];
    entries.sort((a, b) => a.d - b.d);
    return entries[0].lane;
  };

  const laneSlot = (lane: Lane, slotIdx: number, textW: number, boxW: number, anchor: XYPoint) => {
    const step = LABEL_H + LABEL_PAD * 2 + 2;
    if (lane === 'left') {
      return {
        x: Math.max(LABEL_MARGIN, siteBounds.minX - 10 - boxW),
        y: Math.min(VIEW_H - LABEL_MARGIN - 1, Math.max(LABEL_MARGIN + LABEL_H, LABEL_MARGIN + LABEL_H + slotIdx * step)),
        textAnchor: 'end' as const,
      };
    }
    if (lane === 'right') {
      return {
        x: Math.min(VIEW_W - LABEL_MARGIN - boxW, siteBounds.maxX + 10),
        y: Math.min(VIEW_H - LABEL_MARGIN - 1, Math.max(LABEL_MARGIN + LABEL_H, LABEL_MARGIN + LABEL_H + slotIdx * step)),
        textAnchor: 'start' as const,
      };
    }
    if (lane === 'top') {
      return {
        x: Math.min(VIEW_W - LABEL_MARGIN - boxW, Math.max(LABEL_MARGIN, anchor.x - boxW / 2)),
        y: Math.max(LABEL_MARGIN + LABEL_H, siteBounds.minY - 8),
        textAnchor: anchor.x >= VIEW_W / 2 ? 'end' as const : 'start' as const,
      };
    }
    return {
      x: Math.min(VIEW_W - LABEL_MARGIN - boxW, Math.max(LABEL_MARGIN, anchor.x - boxW / 2)),
      y: Math.min(VIEW_H - LABEL_MARGIN - 1, siteBounds.maxY + LABEL_H + 8),
      textAnchor: anchor.x >= VIEW_W / 2 ? 'end' as const : 'start' as const,
    };
  };

  const buildStraightLeaderSegs = (anchor: XYPoint, lineEnd: XYPoint): Array<{ a: XYPoint; b: XYPoint }> => [{ a: anchor, b: lineEnd }];
  const buildElbowSegs = (
    anchor: XYPoint,
    textX: number,
    textY: number,
    textAnchor: 'start' | 'end',
    lane: Lane,
  ): Array<{ a: XYPoint; b: XYPoint }> => {
    const lineEnd = { x: textAnchor === 'start' ? textX - 1.5 : textX + 1.5, y: textY - Math.max(3, LABEL_H * 0.45) };
    if (lane === 'left' || lane === 'right') {
      const side = textAnchor === 'start' ? -1 : 1;
      const elbow = { x: lineEnd.x + side * LEADER_ELBOW_STANDOFF, y: lineEnd.y };
      return [{ a: anchor, b: elbow }, { a: elbow, b: lineEnd }];
    }
    const verticalSign = lane === 'top' ? -1 : 1;
    const elbow = { x: lineEnd.x, y: lineEnd.y + verticalSign * LEADER_ELBOW_STANDOFF };
    return [{ a: anchor, b: elbow }, { a: elbow, b: lineEnd }];
  };
  const segmentsCrossGeometry = (leaderSegs: Array<{ a: XYPoint; b: XYPoint }>): boolean =>
    drawnSegments.some((seg) => leaderSegs.some((lead) => segmentsIntersect(seg.a, seg.b, lead.a, lead.b)));
  const lineEndForText = (textX: number, textY: number, textAnchor: 'start' | 'end'): XYPoint => ({
    x: textAnchor === 'start' ? textX - 1.5 : textX + 1.5,
    y: textY - Math.max(3, LABEL_H * 0.45),
  });
  const leaderPenalty = (leaderLength: number): number => {
    if (leaderLength <= LEADER_SOFT_MAX) return 0;
    return Math.pow(leaderLength - LEADER_SOFT_MAX, 1.15) * LEADER_RAMP_PENALTY;
  };
  const crossCanvasPenalty = (anchor: XYPoint, textX: number): number => {
    const dx = Math.abs(textX - anchor.x);
    if (dx <= CROSS_CANVAS_SOFT_X) return 0;
    return (dx - CROSS_CANVAS_SOFT_X) * 1.25;
  };
  const laneOrderViolationPenalty = (
    laneName: Lane,
    anchor: XYPoint,
    textX: number,
    textY: number,
    annotations: Placed[],
  ): number => {
    for (const ann of annotations) {
      if (ann.lane !== laneName) continue;
      if (laneName === 'left' || laneName === 'right') {
        const shouldBeAbove = anchor.y < ann.anchor.y - 0.1;
        const shouldBeBelow = anchor.y > ann.anchor.y + 0.1;
        if ((shouldBeAbove && textY > ann.textY + 0.1) || (shouldBeBelow && textY < ann.textY - 0.1)) {
          return 10000;
        }
        continue;
      }
      const shouldBeLeft = anchor.x < ann.anchor.x - 0.1;
      const shouldBeRight = anchor.x > ann.anchor.x + 0.1;
      if ((shouldBeLeft && textX > ann.textX + 0.1) || (shouldBeRight && textX < ann.textX - 0.1)) {
        return 10000;
      }
    }
    return 0;
  };

  const typeRankById = new Map(candidates.map((c, idx) => [c.id, idx]));
  const candidatesWithLane = candidates.map((c) => ({
    ...c,
    primaryLaneCached: primaryLane(c.anchors[0]),
  }));
  const laneBuckets = new Map<Lane, typeof candidatesWithLane>();
  for (const candidate of candidatesWithLane) {
    const bucket = laneBuckets.get(candidate.primaryLaneCached) ?? [];
    bucket.push(candidate);
    laneBuckets.set(candidate.primaryLaneCached, bucket);
  }
  for (const [lane, bucket] of laneBuckets) {
    bucket.sort((a, b) => {
      const axisDelta = (lane === 'left' || lane === 'right')
        ? a.anchors[0].y - b.anchors[0].y
        : a.anchors[0].x - b.anchors[0].x;
      if (Math.abs(axisDelta) > 1e-6) return axisDelta;
      return (typeRankById.get(a.id) ?? 0) - (typeRankById.get(b.id) ?? 0);
    });
  }
  const slotIdxByCandidateId = new Map<string, number>();
  for (const bucket of laneBuckets.values()) {
    bucket.forEach((candidate, idx) => slotIdxByCandidateId.set(candidate.id, idx));
  }
  const slotIdxForCandidateInLane = (
    candidate: typeof candidates[number],
    lane: Lane,
  ): number => {
    const primaryLaneSlot = slotIdxByCandidateId.get(candidate.id) ?? 0;
    if (primaryLane(candidate.anchors[0]) === lane) return primaryLaneSlot;
    const cands = laneBuckets.get(lane);
    if (!cands) return 0;
    const peers = cands
      .filter((peer) => peer.id !== candidate.id)
      .map((peer) => peer.anchors[0]);
    return laneAxisRank(candidate.anchors[0], lane, peers);
  };

  const buildForcedOption = (
    candidate: typeof candidates[number],
    state: State,
  ): Placed | null => {
    const anchor = candidate.anchors[0];
    const text = candidate.shortName;
    const textW = Math.max(14, text.length * LABEL_CHAR_W);
    const boxW = textW + LABEL_PAD * 2;
    const boxH = LABEL_H + LABEL_PAD * 2;
    const lane = primaryLane(anchor);
    const laneOrder: Lane[] = [lane, 'left', 'right', 'top', 'bottom'].filter((v, i, arr) => arr.indexOf(v) === i) as Lane[];
    let best: Placed | null = null;
    for (const laneName of laneOrder) {
      const slotIdx = slotIdxForCandidateInLane(candidate, laneName);
      const base = laneSlot(laneName, slotIdx, textW, boxW, anchor);
      const ySweep = Array.from({ length: Math.max(1, Math.floor((VIEW_H - LABEL_MARGIN * 2) / 4)) }, (_, i) => LABEL_MARGIN + LABEL_H + i * 4);
      ySweep.sort((a, b) => Math.abs(a - anchor.y) - Math.abs(b - anchor.y));
      for (const sweepY of ySweep) {
        const y = (laneName === 'left' || laneName === 'right')
          ? Math.min(VIEW_H - LABEL_MARGIN - 1, Math.max(LABEL_MARGIN + LABEL_H, sweepY))
          : base.y;
        const x = base.x;
        const textAnchor = base.textAnchor;
        const textX = textAnchor === 'start' ? x + LABEL_PAD : x + boxW - LABEL_PAD;
        const textY = y;
        const box: Rect = {
          x: textAnchor === 'start' ? textX - LABEL_PAD : textX - boxW + LABEL_PAD,
          y: textY - LABEL_H + 1 - LABEL_PAD,
          w: boxW,
          h: boxH,
        };
        if (box.x < LABEL_MARGIN || box.y < LABEL_MARGIN || box.x + box.w > VIEW_W - LABEL_MARGIN || box.y + box.h > VIEW_H - LABEL_MARGIN) continue;
        const spacingBox = inflateRect(box, 2.3);
        if (state.occupiedRects.some((r) => rectIntersects(r, spacingBox))) continue;
        if (siteBoundaryPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly))) continue;
        const boxHitsContainer = containerPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
        const boxHitsAsset = assetPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
        if (boxHitsAsset) continue;
        const lineEnd = lineEndForText(textX, textY, textAnchor);
        const leaderLength = Math.hypot(lineEnd.x - anchor.x, lineEnd.y - anchor.y);
        if (leaderLength < MIN_LEADER_LENGTH || leaderLength > MAX_LEADER_LENGTH + 24) continue;
        if (laneOrderViolationPenalty(laneName, anchor, textX, textY, state.annotations) >= 10000) continue;
        const elbowSegs = buildElbowSegs(anchor, textX, textY, textAnchor, laneName);
        const elbowBlocked = segmentsCrossGeometry(elbowSegs)
          || anyLeaderSegmentHitsRects(elbowSegs, state.occupiedRects)
          || state.leaderSegs.some((ex) => elbowSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)));
        const leaderSegs = elbowBlocked ? buildStraightLeaderSegs(anchor, lineEnd) : elbowSegs;
        if (anyLeaderSegmentHitsRects(leaderSegs, state.occupiedRects)) continue;
        if (state.leaderSegs.some((ex) => leaderSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)))) continue;
        const laneDistancePenalty = laneName === lane ? 0 : 26;
        const sideOffsetPenalty = (laneName === 'left' || laneName === 'right')
          ? Math.abs(textX - anchor.x) * 0.85
          : 0;
        const candidateScore = laneDistancePenalty
          + sideOffsetPenalty
          + crossCanvasPenalty(anchor, textX)
          + (boxHitsContainer ? 120 : 0)
          + Math.abs(leaderLength - LABEL_DISTANCE_TARGET) * 0.9
          + leaderPenalty(leaderLength);
        const placed: Placed = {
          id: candidate.id,
          name: text,
          fullName: candidate.name,
          anchor,
          role: candidate.role,
          textX,
          textY,
          textAnchor,
          lineEndX: lineEnd.x,
          lineEndY: lineEnd.y,
          lane: laneName,
          box,
          spacingBox,
          leaderSegs,
          score: 180 + candidateScore,
        };
        if (!best || placed.score < best.score) best = placed;
      }
    }
    return best;
  };

  const buildHardFallbackOption = (
    candidate: typeof candidates[number],
    state: State,
  ): Placed => {
    const anchor = candidate.anchors[0];
    const text = candidate.shortName;
    const textW = Math.max(14, text.length * LABEL_CHAR_W);
    const boxW = textW + LABEL_PAD * 2;
    const boxH = LABEL_H + LABEL_PAD * 2;
    const lane = primaryLane(anchor);
    const laneOrder: Lane[] = [lane, 'left', 'right', 'top', 'bottom'].filter((v, i, arr) => arr.indexOf(v) === i) as Lane[];
    let best: { x: number; y: number; textAnchor: 'start' | 'end'; score: number; lane: Lane } | null = null;
    for (const laneName of laneOrder) {
      const slotIdx = slotIdxForCandidateInLane(candidate, laneName);
      const base = laneSlot(laneName, slotIdx, textW, boxW, anchor);
      const ySweep = Array.from({ length: Math.max(1, Math.floor((VIEW_H - LABEL_MARGIN * 2) / 4)) }, (_, i) => LABEL_MARGIN + LABEL_H + i * 4);
      for (const sweepY of ySweep) {
        const y = (laneName === 'left' || laneName === 'right')
          ? Math.min(VIEW_H - LABEL_MARGIN - 1, Math.max(LABEL_MARGIN + LABEL_H, sweepY))
          : base.y;
        const x = base.x;
        const textAnchor = base.textAnchor;
        const textX = textAnchor === 'start' ? x + LABEL_PAD : x + boxW - LABEL_PAD;
        const textY = y;
        const box: Rect = {
          x: textAnchor === 'start' ? textX - LABEL_PAD : textX - boxW + LABEL_PAD,
          y: textY - LABEL_H + 1 - LABEL_PAD,
          w: boxW,
          h: boxH,
        };
        if (box.x < LABEL_MARGIN || box.y < LABEL_MARGIN || box.x + box.w > VIEW_W - LABEL_MARGIN || box.y + box.h > VIEW_H - LABEL_MARGIN) continue;
        const spacingBox = inflateRect(box, 1.8);
        const orderPenalty = laneOrderViolationPenalty(laneName, anchor, textX, textY, state.annotations);
        const overlapPenalty = state.occupiedRects.filter((r) => rectIntersects(r, spacingBox)).length * 100;
        const leaderLength = Math.hypot(textX - anchor.x, textY - anchor.y) * 0.7;
        const boxHitsContainer = containerPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
        const boxHitsAsset = assetPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
        const score = overlapPenalty
          + orderPenalty
          + leaderLength
          + (boxHitsContainer ? 120 : 0)
          + (boxHitsAsset ? 280 : 0)
          + crossCanvasPenalty(anchor, textX)
          + leaderPenalty(Math.hypot(textX - anchor.x, textY - anchor.y));
        if (!best || score < best.score) {
          best = { x, y, textAnchor, score, lane: laneName };
        }
      }
    }
    const chosen = best ?? {
      x: Math.min(VIEW_W - LABEL_MARGIN - boxW, Math.max(LABEL_MARGIN, anchor.x + 10)),
      y: Math.min(VIEW_H - LABEL_MARGIN - 1, Math.max(LABEL_MARGIN + LABEL_H, anchor.y)),
      textAnchor: anchor.x >= VIEW_W / 2 ? 'end' as const : 'start' as const,
      score: 0,
      lane,
    };
    const textX = chosen.textAnchor === 'start' ? chosen.x + LABEL_PAD : chosen.x + boxW - LABEL_PAD;
    const textY = chosen.y;
    const box: Rect = {
      x: chosen.textAnchor === 'start' ? textX - LABEL_PAD : textX - boxW + LABEL_PAD,
      y: textY - LABEL_H + 1 - LABEL_PAD,
      w: boxW,
      h: boxH,
    };
    const spacingBox = inflateRect(box, 1.8);
    const lineEnd = lineEndForText(textX, textY, chosen.textAnchor);
    const elbowSegs = buildElbowSegs(anchor, textX, textY, chosen.textAnchor, chosen.lane);
    const elbowBlocked = segmentsCrossGeometry(elbowSegs)
      || anyLeaderSegmentHitsRects(elbowSegs, state.occupiedRects)
      || state.leaderSegs.some((ex) => elbowSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)));
    const leaderSegs = elbowBlocked ? buildStraightLeaderSegs(anchor, lineEnd) : elbowSegs;
    return {
      id: candidate.id,
      name: text,
      fullName: candidate.name,
      anchor,
      role: candidate.role,
      textX,
      textY,
      textAnchor: chosen.textAnchor,
      lineEndX: lineEnd.x,
      lineEndY: lineEnd.y,
      lane: chosen.lane,
      box,
      spacingBox,
      leaderSegs,
      score: 260,
    };
  };

  const orderedCandidates = candidates;

  const buildOptions = (candidate: typeof candidates[number], state: State): Placed[] => {
    const opts: Placed[] = [];
    const lane = primaryLane(candidate.anchors[0]);
    const laneOrder: Lane[] = [lane, 'left', 'right', 'top', 'bottom'].filter((v, i, arr) => arr.indexOf(v) === i) as Lane[];
    const labels = [
      { text: candidate.name, shortened: false },
      { text: candidate.shortName, shortened: candidate.shortName !== candidate.name },
    ];
    for (const anchor of candidate.anchors) {
      for (const label of labels) {
        const textW = Math.max(14, label.text.length * LABEL_CHAR_W);
        const boxW = textW + LABEL_PAD * 2;
        const boxH = LABEL_H + LABEL_PAD * 2;
        const nearAnchorOffsets: Array<{ dx: number; dy: number; textAnchor: 'start' | 'end' }> = [
          { dx: 14, dy: -10, textAnchor: 'start' },
          { dx: 14, dy: 10, textAnchor: 'start' },
          { dx: -14, dy: -10, textAnchor: 'end' },
          { dx: -14, dy: 10, textAnchor: 'end' },
          { dx: 22, dy: 0, textAnchor: 'start' },
          { dx: -22, dy: 0, textAnchor: 'end' },
        ];
        for (const near of nearAnchorOffsets) {
          const textAnchor = near.textAnchor;
          const textX = anchor.x + near.dx;
          const textY = anchor.y + near.dy;
          const box: Rect = {
            x: textAnchor === 'start' ? textX - LABEL_PAD : textX - boxW + LABEL_PAD,
            y: textY - LABEL_H + 1 - LABEL_PAD,
            w: boxW,
            h: boxH,
          };
          if (box.x < LABEL_MARGIN || box.y < LABEL_MARGIN || box.x + box.w > VIEW_W - LABEL_MARGIN || box.y + box.h > VIEW_H - LABEL_MARGIN) continue;
          const spacingBox = inflateRect(box, 2.6);
          if (state.occupiedRects.some((r) => rectIntersects(r, spacingBox))) continue;
          const lineEnd = lineEndForText(textX, textY, textAnchor);
          const leaderLength = Math.hypot(lineEnd.x - anchor.x, lineEnd.y - anchor.y);
          if (leaderLength < MIN_LEADER_LENGTH || leaderLength > MAX_LEADER_LENGTH) continue;
          const elbowSegs = buildElbowSegs(anchor, textX, textY, textAnchor, lane);
          const elbowBlocked = segmentsCrossGeometry(elbowSegs)
            || anyLeaderSegmentHitsRects(elbowSegs, state.occupiedRects)
            || state.leaderSegs.some((ex) => elbowSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)));
          const leaderSegs = elbowBlocked ? buildStraightLeaderSegs(anchor, lineEnd) : elbowSegs;
          if (anyLeaderSegmentHitsRects(leaderSegs, state.occupiedRects)) continue;
          if (state.leaderSegs.some((ex) => leaderSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)))) continue;
          const crossesGeometry = segmentsCrossGeometry(leaderSegs);
          const boxHitsGeom = drawnSegments.some((seg) => rectIntersectsSegment(inflateRect(box, 0.8), seg.a, seg.b));
          const boxHitsContainer = containerPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
          const boxHitsAsset = assetPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
          const boxHitsSite = siteBoundaryPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
          const tinyPolygon = candidate.geometryType === 'polygon' && candidate.projectedAreaSqPx > 0 && candidate.projectedAreaSqPx < 300;
          const ownPolygon = assetPolygonByLayerId.get(candidate.id);
          const tinyPolygonInside = tinyPolygon && ownPolygon ? rectIntersectsPolygon(spacingBox, ownPolygon) : false;
          if (boxHitsSite) continue;
          if (boxHitsAsset) continue;
          if (tinyPolygonInside) continue;
          let score = 0;
          if (crossesGeometry) score += 140;
          if (boxHitsGeom) score += 120;
          if (boxHitsContainer) score += 90;
          score += Math.abs(leaderLength - LABEL_DISTANCE_TARGET) * 0.9;
          score += crossCanvasPenalty(anchor, textX);
          score += leaderPenalty(leaderLength);
          if (label.shortened) score += 8;
          opts.push({
            id: candidate.id,
            name: label.text,
            fullName: candidate.name,
            anchor,
            role: candidate.role,
            textX,
            textY,
            textAnchor,
            lineEndX: lineEnd.x,
            lineEndY: lineEnd.y,
            lane,
            box,
            spacingBox,
            leaderSegs,
            score,
          });
        }
        for (let laneIdx = 0; laneIdx < laneOrder.length; laneIdx++) {
          const laneName = laneOrder[laneIdx];
          const slotIdx = slotIdxForCandidateInLane(candidate, laneName);
          const base = laneSlot(laneName, slotIdx, textW, boxW, anchor);
          for (const delta of [-14, -8, -4, 0, 4, 8, 14]) {
            const x = base.x + ((laneName === 'top' || laneName === 'bottom') ? delta * 0.6 : 0);
            const y = base.y + ((laneName === 'left' || laneName === 'right') ? delta : 0);
            const textAnchor = base.textAnchor;
            const textX = textAnchor === 'start' ? x + LABEL_PAD : x + boxW - LABEL_PAD;
            const textY = y;
            const box: Rect = {
              x: textAnchor === 'start' ? textX - LABEL_PAD : textX - boxW + LABEL_PAD,
              y: textY - LABEL_H + 1 - LABEL_PAD,
              w: boxW,
              h: boxH,
            };
            if (box.x < LABEL_MARGIN || box.y < LABEL_MARGIN || box.x + box.w > VIEW_W - LABEL_MARGIN || box.y + box.h > VIEW_H - LABEL_MARGIN) continue;
            const spacingBox = inflateRect(box, 2.6);
            if (state.occupiedRects.some((r) => rectIntersects(r, spacingBox))) continue;
            const lineEnd = lineEndForText(textX, textY, textAnchor);
            const leaderLength = Math.hypot(lineEnd.x - anchor.x, lineEnd.y - anchor.y);
            if (leaderLength < MIN_LEADER_LENGTH || leaderLength > MAX_LEADER_LENGTH) continue;
            if (laneOrderViolationPenalty(laneName, anchor, textX, textY, state.annotations) >= 10000) continue;
            const elbowSegs = buildElbowSegs(anchor, textX, textY, textAnchor, laneName);
            const elbowBlocked = segmentsCrossGeometry(elbowSegs)
              || anyLeaderSegmentHitsRects(elbowSegs, state.occupiedRects)
              || state.leaderSegs.some((ex) => elbowSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)));
            const leaderSegs = elbowBlocked ? buildStraightLeaderSegs(anchor, lineEnd) : elbowSegs;
            if (anyLeaderSegmentHitsRects(leaderSegs, state.occupiedRects)) continue;
            if (state.leaderSegs.some((ex) => leaderSegs.some((seg) => segmentsIntersect(ex.a, ex.b, seg.a, seg.b)))) continue;
            const crossesGeometry = segmentsCrossGeometry(leaderSegs);
            const boxHitsGeom = drawnSegments.some((seg) => rectIntersectsSegment(inflateRect(box, 0.8), seg.a, seg.b));
            const boxHitsContainer = containerPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
            const boxHitsAsset = assetPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
            const boxHitsSite = siteBoundaryPolygonsProjected.some((poly) => rectIntersectsPolygon(spacingBox, poly));
            const tinyPolygon = candidate.geometryType === 'polygon' && candidate.projectedAreaSqPx > 0 && candidate.projectedAreaSqPx < 300;
            const ownPolygon = assetPolygonByLayerId.get(candidate.id);
            const tinyPolygonInside = tinyPolygon && ownPolygon ? rectIntersectsPolygon(spacingBox, ownPolygon) : false;
            if (boxHitsSite) continue;
            if (boxHitsAsset) continue;
            if (tinyPolygonInside) continue;
            let score = 0;
            if (crossesGeometry) score += 140;
            if (boxHitsGeom) score += 120;
            if (boxHitsContainer) score += 90;
            score += Math.abs(leaderLength - LABEL_DISTANCE_TARGET) * 0.9;
            score += crossCanvasPenalty(anchor, textX);
            score += leaderPenalty(leaderLength);
            if (laneName === 'left' || laneName === 'right') score += Math.abs(textX - anchor.x) * 0.65;
            score += laneIdx * 6;
            if (label.shortened) score += 8;
            if (leaderSegs.length > 1) score += 6;
            opts.push({
              id: candidate.id,
              name: label.text,
              fullName: candidate.name,
              anchor,
              role: candidate.role,
              textX,
              textY,
              textAnchor,
              lineEndX: lineEnd.x,
              lineEndY: lineEnd.y,
              lane: laneName,
              box,
              spacingBox,
              leaderSegs,
              score,
            });
          }
        }
      }
    }
    opts.sort((a, b) => a.score - b.score);
    return opts.slice(0, MAX_OPTIONS_PER_LABEL);
  };

  let beam: State[] = [{ score: 0, annotations: [], occupiedRects: [], leaderSegs: [] }];
  for (let i = 0; i < orderedCandidates.length; i++) {
    const candidate = orderedCandidates[i];
    const next: State[] = [];
    for (const state of beam) {
      const opts = buildOptions(candidate, state);
      if (opts.length === 0) {
        // LABEL-DECLUTTER step 4 — no collision-free slot for this candidate.
        // Only FORCE-PLACE (stack on top of others as a last resort) for the
        // top-priority half of the capped list, so a substation never silently
        // disappears. Lower-priority overflow is DROPPED instead of stacked —
        // the state passes through unchanged, removing the guaranteed-collision
        // behaviour that made dense maps illegible.
        if (forcePlaceIds.has(candidate.id)) {
          const forced = buildForcedOption(candidate, state);
          const chosen = forced ?? buildHardFallbackOption(candidate, state);
          next.push({
            score: state.score + chosen.score,
            annotations: [...state.annotations, chosen],
            occupiedRects: [...state.occupiedRects, chosen.spacingBox],
            leaderSegs: [...state.leaderSegs, ...chosen.leaderSegs],
          });
        } else {
          next.push(state);
        }
        continue;
      }
      for (const opt of opts) {
        next.push({
          score: state.score + opt.score,
          annotations: [...state.annotations, opt],
          occupiedRects: [...state.occupiedRects, opt.spacingBox],
          leaderSegs: [...state.leaderSegs, ...opt.leaderSegs],
        });
      }
    }
    if (next.length === 0) break;
    next.sort((a, b) => a.score - b.score);
    beam = next.slice(0, BEAM_WIDTH);
  }
  const best = beam[0] ?? { score: 0, annotations: [], occupiedRects: [], leaderSegs: [] };
  const compactLaneAnnotations = (input: Placed[]): Placed[] => {
    const next = input.map((ann) => ({
      ...ann,
      box: { ...ann.box },
      spacingBox: { ...ann.spacingBox },
      leaderSegs: ann.leaderSegs.map((seg) => ({ a: { ...seg.a }, b: { ...seg.b } })),
    }));
    for (const laneName of ['left', 'right', 'top', 'bottom'] as const) {
      const members = next.filter((ann) => ann.lane === laneName);
      if (members.length <= 1) continue;
      if (laneName === 'left' || laneName === 'right') {
        members.sort((a, b) => a.textY - b.textY);
        let cursor = LABEL_MARGIN + LABEL_H;
        for (const ann of members) {
          if (ann.textY < cursor) ann.textY = cursor;
          cursor = ann.textY + COMPACTION_GAP_Y;
        }
        const maxY = VIEW_H - LABEL_MARGIN - 1;
        const overflow = members[members.length - 1].textY - maxY;
        if (overflow > 0) {
          for (const ann of members) ann.textY -= overflow;
        }
        for (const ann of members) {
          ann.textY = Math.min(maxY, Math.max(LABEL_MARGIN + LABEL_H, ann.textY));
        }
      } else {
        members.sort((a, b) => a.textX - b.textX);
        let cursor = LABEL_MARGIN + LABEL_PAD;
        for (const ann of members) {
          if (ann.textX < cursor) ann.textX = cursor;
          cursor = ann.textX + COMPACTION_GAP_X;
        }
        const maxX = VIEW_W - LABEL_MARGIN - LABEL_PAD;
        const overflow = members[members.length - 1].textX - maxX;
        if (overflow > 0) {
          for (const ann of members) ann.textX -= overflow;
        }
        for (const ann of members) {
          ann.textX = Math.min(maxX, Math.max(LABEL_MARGIN + LABEL_PAD, ann.textX));
        }
      }
      for (const ann of members) {
        const boxW = ann.box.w;
        const boxH = ann.box.h;
        ann.box = {
          x: ann.textAnchor === 'start' ? ann.textX - LABEL_PAD : ann.textX - boxW + LABEL_PAD,
          y: ann.textY - LABEL_H + 1 - LABEL_PAD,
          w: boxW,
          h: boxH,
        };
        ann.spacingBox = inflateRect(ann.box, laneName === 'left' || laneName === 'right' ? 2.6 : 2.2);
        const lineEnd = lineEndForText(ann.textX, ann.textY, ann.textAnchor);
        ann.lineEndX = lineEnd.x;
        ann.lineEndY = lineEnd.y;
        ann.leaderSegs = buildElbowSegs(ann.anchor, ann.textX, ann.textY, ann.textAnchor, laneName);
      }
    }
    return next;
  };
  const compactedBest = compactLaneAnnotations(best.annotations);
  const annotations: Annotation[] = compactedBest.map((a) => ({
    id: a.id,
    name: a.name,
    fullName: a.fullName,
    anchor: a.anchor,
    role: a.role,
    textX: a.textX,
    textY: a.textY,
    textAnchor: a.textAnchor,
    lineEndX: a.lineEndX,
    lineEndY: a.lineEndY,
    lane: a.lane,
    leaderSegs: a.leaderSegs,
  }));
  const compactedOccupiedRects = compactedBest.map((a) => a.spacingBox);
  const compactedLeaderSegs = compactedBest.flatMap((a) => a.leaderSegs);
  occupied.push(...compactedOccupiedRects);
  placedLeaderSegments.push(...compactedLeaderSegs);
  // HERO-COVER CLEAN-MAP — drop the per-polygon area "ac" labels entirely when
  // `hideAllLabels` is set (geometry-only diagram). `assetAreaRects` then stays
  // empty so the legend placement no longer reserves space for absent chips.
  // NB: keyed off `hideAllLabels` ONLY, NOT `labelsMode='none'` — the latter's
  // contract keeps the area chips + utility legend (only leader NAMES drop).
  const { labels: assetAreaLabels, rects: assetAreaRects } = hideAllLabels
    ? { labels: [], rects: [] }
    : buildAreaLabels(compactedOccupiedRects, compactedLeaderSegs);
  const annotationRects = compactedBest.map((a) => a.box);
  const legendLayout = (() => {
    // HERO-COVER CLEAN-MAP — the utility legend chips carry feature NAMES
    // ("Entergy 230kV", "Long-haul Fiber Corridor", …). Suppress the whole
    // legend when `hideAllLabels` is set so the diagram is geometry-only.
    // Scoped to `hideAllLabels` ONLY — `labelsMode='none'` keeps the legend.
    if (hideAllLabels) return null;
    if (utilityLegend.length === 0) return null;
    const rowGap = 1.2;
    const rowH = AREA_LABEL_FONT_SIZE + 3.2;
    const dashW = 8;
    const chipPad = 2;
    const maxNameW = Math.max(...utilityLegend.map((item) => Math.max(10, item.name.length * AREA_LABEL_CHAR_W)));
    const width = Math.max(38, chipPad * 2 + dashW + 2.5 + maxNameW);
    const height = chipPad * 2 + utilityLegend.length * rowH + Math.max(0, utilityLegend.length - 1) * rowGap;
    const corners = [
      { x: VIEW_W - LABEL_MARGIN - width, y: VIEW_H - LABEL_MARGIN - height },
      { x: VIEW_W - LABEL_MARGIN - width, y: LABEL_MARGIN },
      { x: LABEL_MARGIN, y: VIEW_H - LABEL_MARGIN - height },
      { x: LABEL_MARGIN, y: LABEL_MARGIN },
    ];
    let bestCorner = corners[0];
    let bestScore = Number.POSITIVE_INFINITY;
    const blockedRects = [...compactedOccupiedRects, ...annotationRects, ...assetAreaRects];
    for (const corner of corners) {
      const box: Rect = { x: corner.x, y: corner.y, w: width, h: height };
      const overlapPenalty = blockedRects.filter((rect) => rectIntersects(inflateRect(rect, 1.2), box)).length * 250;
      const boundaryPenalty = siteBoundaryPolygonsProjected.some((poly) => rectIntersectsPolygon(inflateRect(box, 0.8), poly)) ? 2000 : 0;
      const centerDist = Math.hypot(corner.x + width / 2 - VIEW_W / 2, corner.y + height / 2 - VIEW_H / 2);
      const score = overlapPenalty + boundaryPenalty - centerDist * 0.15;
      if (score < bestScore) {
        bestScore = score;
        bestCorner = corner;
      }
    }
    return { x: bestCorner.x, y: bestCorner.y, rowH, rowGap, chipPad, dashW };
  })();
  return (
    <div
      className={cn('w-full max-w-md aspect-[2/1]', onDark ? 'opacity-90' : 'opacity-85', className)}
      data-testid="cover-hero-boundary-overlay"
      // Chromium paints SVG labels inside an aria-hidden diagram as unmarked
      // PDF text. This explicit marker lets the tagged-render audit prove each
      // painted label came from this reviewed decorative root, page by page.
      data-pdf-decorative-text
      aria-hidden
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="block w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {hasFillGradient && (
          <defs>
            <linearGradient
              id={`${HERO_FILL_GRADIENT_PREFIX}-${fillGradientId}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              data-testid="cover-hero-fill-gradient"
            >
              {gradientStops.map((c, idx) => (
                <stop
                  key={`hero-fill-stop-${idx}`}
                  offset={`${Math.round((idx / (gradientStops.length - 1)) * 100)}%`}
                  stopColor={c}
                />
              ))}
            </linearGradient>
          </defs>
        )}
        {layers.map((layer, idx) => {
          const stroke = colorForCategory(layer.category, layer.color);
          const points = boundaryToSvgPoints(layer.geometry.coordinates, VIEW_W, VIEW_H, PAD, combinedBbox);
          if (!points) return null;
          if (layer.geometry.type === 'point') {
            const [xy] = points.split(' ');
            const [cx, cy] = xy.split(',').map(Number);
            return <circle key={`layer-point-${idx}`} cx={cx} cy={cy} r={2.5} fill={stroke} opacity={0.95} />;
          }
          if (layer.geometry.type === 'line') {
            return (
              <polyline
                key={`layer-line-${idx}`}
                points={points}
                fill="none"
                stroke={stroke}
                strokeWidth={1.3 * geomStrokeScale}
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity={0.9}
                vectorEffect="non-scaling-stroke"
              />
            );
          }
          return (
            <polygon
              key={`layer-poly-${idx}`}
              points={points}
              fill={polygonRoleById.get(layer.id) === 'container' ? 'none' : stroke}
              fillOpacity={polygonRoleById.get(layer.id) === 'container' ? 0 : 0.18}
              stroke={polygonRoleById.get(layer.id) === 'container' ? (onDark ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.55)') : stroke}
              strokeWidth={(polygonRoleById.get(layer.id) === 'container' ? 1.15 : 1.35) * geomStrokeScale}
              strokeLinejoin="round"
              opacity={0.95}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {parcels.map((p, i) => {
          const points = boundaryToSvgPoints(p.polygon, VIEW_W, VIEW_H, PAD, combinedBbox);
          if (!points) return null;
          // SBPV-PALETTE — colour each parcel from the opt-in palette when
          // a host supplies one (so its legend can match). With no palette
          // (the report hero cover), fall back to the tone-derived
          // `strokeClass` exactly as before — byte-identical output.
          // Stroke precedence: per-parcel palette > explicit hero strokeColor
          // > tone-derived strokeClass. Fill: optional hero gradient, else none.
          const parcelStroke = parcelStrokeColors && parcelStrokeColors.length > 0
            ? parcelStrokeColors[i % parcelStrokeColors.length]
            : strokeColor;
          return (
            <polygon
              key={p.id}
              points={points}
              fill={parcelFillUrl}
              fillOpacity={hasFillGradient ? 0.7 : 0}
              className={parcelStroke ? undefined : strokeClass}
              stroke={parcelStroke}
              strokeWidth={1.75 * geomStrokeScale}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {annotations.map((ann) => (
          <g
            key={`ann-${ann.id}`}
            data-testid={`cover-hero-layer-indicator-${ann.id}`}
            data-anchor-x={ann.anchor.x.toFixed(1)}
            data-anchor-y={ann.anchor.y.toFixed(1)}
          >
            <polyline
              points={[ann.leaderSegs[0].a, ...ann.leaderSegs.map((seg) => seg.b)]
                .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
                .join(' ')}
              fill="none"
              stroke={`rgba(${onDark ? '255,255,255' : '15,23,42'},${(() => {
                const leaderLength = Math.hypot(ann.lineEndX - ann.anchor.x, ann.lineEndY - ann.anchor.y);
                if (leaderLength <= LEADER_SOFT_MAX) return 0.75;
                const reduced = 0.75 - Math.min(0.2, (leaderLength - LEADER_SOFT_MAX) / 120);
                return Math.max(0.55, Number(reduced.toFixed(2)));
              })()})`}
              strokeWidth={0.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <text
              x={ann.textX}
              y={ann.textY}
              textAnchor={ann.textAnchor}
              fill={onDark ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.95)'}
              fontSize={LABEL_FONT_SIZE}
              fontWeight={400}
              stroke={onDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.65)'}
              strokeWidth={0.6}
              paintOrder="stroke"
              fontFamily="Inter, sans-serif"
              data-full-label={ann.fullName}
            >
              {ann.name}
            </text>
          </g>
        ))}
        {assetAreaLabels.map((label) => (
          <text
            key={`area-${label.id}`}
            x={label.x}
            y={label.y}
            textAnchor="start"
            fill={onDark ? 'rgba(255,255,255,0.9)' : 'rgba(15,23,42,0.92)'}
            fontSize={AREA_LABEL_FONT_SIZE}
            fontWeight={450}
            stroke={onDark ? 'rgba(0,0,0,0.34)' : 'rgba(255,255,255,0.6)'}
            strokeWidth={0.45}
            paintOrder="stroke"
            fontFamily="Inter, sans-serif"
            data-testid={`cover-hero-asset-area-${label.id}`}
          >
            {label.text}
          </text>
        ))}
        {legendLayout && (
          <g
            data-testid="cover-hero-utility-legend"
            transform={`translate(${legendLayout.x.toFixed(1)} ${legendLayout.y.toFixed(1)})`}
          >
            {utilityLegend.map((item, idx) => {
              const rowY = legendLayout.chipPad + idx * (legendLayout.rowH + legendLayout.rowGap);
              return (
                <g key={`utility-legend-${item.id}`} data-testid={`cover-hero-utility-legend-chip-${item.id}`}>
                  <line
                    x1={legendLayout.chipPad}
                    y1={rowY + legendLayout.rowH * 0.5}
                    x2={legendLayout.chipPad + legendLayout.dashW}
                    y2={rowY + legendLayout.rowH * 0.5}
                    stroke={item.color}
                    strokeWidth={item.lineWidth}
                    strokeLinecap="round"
                  />
                  <text
                    x={legendLayout.chipPad + legendLayout.dashW + 2.5}
                    y={rowY + legendLayout.rowH * 0.72}
                    textAnchor="start"
                    fill={onDark ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.95)'}
                    fontSize={AREA_LABEL_FONT_SIZE}
                    fontWeight={450}
                    stroke={onDark ? 'rgba(0,0,0,0.32)' : 'rgba(255,255,255,0.6)'}
                    strokeWidth={0.4}
                    paintOrder="stroke"
                    fontFamily="Inter, sans-serif"
                  >
                    {item.name}
                  </text>
                </g>
              );
            })}
          </g>
        )}
        {coverDebugEnabled && (
          <g data-testid="cover-hero-debug-overlay">
            {candidates.flatMap((candidate) =>
              candidate.anchors.map((anchor, idx) => (
                <circle
                  key={`debug-candidate-${candidate.id}-${idx}`}
                  data-testid={`cover-hero-debug-anchor-candidate-${candidate.id}-${idx}`}
                  cx={anchor.x}
                  cy={anchor.y}
                  r={0.95}
                  fill="rgba(255,0,0,0.95)"
                />
              )))}
            {annotations.map((ann) => (
              <circle
                key={`debug-chosen-${ann.id}`}
                data-testid={`cover-hero-debug-anchor-chosen-${ann.id}`}
                cx={ann.anchor.x}
                cy={ann.anchor.y}
                r={1.15}
                fill="rgba(0,255,120,0.95)"
              />
            ))}
            {annotationRects.map((rect, idx) => (
              <rect
                key={`debug-label-box-${idx}`}
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                fill="none"
                stroke="rgba(0,255,255,0.8)"
                strokeWidth={0.38}
              />
            ))}
            {drawnSegments.map((seg, idx) => (
              <line
                key={`debug-segment-${idx}`}
                x1={seg.a.x}
                y1={seg.a.y}
                x2={seg.b.x}
                y2={seg.b.y}
                stroke="rgba(255,255,255,0.16)"
                strokeWidth={0.35}
              />
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}
